import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Cookie, Mail, Calendar } from 'lucide-react';
import SEO from '../../components/SEO';

const CookiePolicyPage = () => {
  const lastUpdated = '17 Aralık 2025';

  return (
    <div className="min-h-screen bg-brand-dark py-12">
      <SEO 
        title="Çerez Politikası"
        description="Borsamex çerez (cookie) politikası. Web sitemizde kullanılan çerezler hakkında bilgi."
        url="/legal/cookies"
      />
      
      <div className="container mx-auto px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 px-3 py-1 bg-orange-500/10 text-orange-400 border-orange-500/20">
            <Cookie className="h-3 w-3 mr-1" /> Çerezler
          </Badge>
          <h1 className="text-4xl font-bold text-white mb-4">
            Çerez Politikası
          </h1>
          <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
            <Calendar className="h-4 w-4" />
            <span>Son güncelleme: {lastUpdated}</span>
          </div>
        </div>

        {/* Content */}
        <Card className="border-slate-800 bg-slate-900/50">
          <CardContent className="p-8 prose prose-invert max-w-none">
            
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">1. Çerez Nedir?</h2>
              <p className="text-slate-300">
                Çerezler (cookies), web sitelerinin tarayıcınıza gönderdiği ve cihazınızda saklanan 
                küçük metin dosyalarıdır. Bu dosyalar, web sitesinin sizi tanımasına, tercihlerinizi 
                hatırlamasına ve size daha iyi bir deneyim sunmasına yardımcı olur.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">2. Kullandığımız Çerez Türleri</h2>
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h3 className="text-lg font-semibold text-primary mb-2">Zorunlu Çerezler</h3>
                  <p className="text-slate-300 text-sm mb-2">
                    Web sitesinin temel işlevleri için gereklidir. Bu çerezler olmadan site düzgün çalışmaz.
                  </p>
                  <ul className="list-disc list-inside text-slate-400 text-sm space-y-1">
                    <li>Oturum yönetimi</li>
                    <li>Güvenlik token'ları</li>
                    <li>Yük dengeleme</li>
                  </ul>
                </div>

                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h3 className="text-lg font-semibold text-blue-400 mb-2">İşlevsel Çerezler</h3>
                  <p className="text-slate-300 text-sm mb-2">
                    Tercihlerinizi hatırlayarak size özelleştirilmiş bir deneyim sunar.
                  </p>
                  <ul className="list-disc list-inside text-slate-400 text-sm space-y-1">
                    <li>Dil tercihi</li>
                    <li>Tema tercihi (koyu/açık mod)</li>
                    <li>Dashboard düzeni</li>
                  </ul>
                </div>

                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h3 className="text-lg font-semibold text-yellow-400 mb-2">Analitik Çerezler</h3>
                  <p className="text-slate-300 text-sm mb-2">
                    Siteyi nasıl kullandığınızı anlamamıza yardımcı olur.
                  </p>
                  <ul className="list-disc list-inside text-slate-400 text-sm space-y-1">
                    <li>Sayfa görüntüleme istatistikleri</li>
                    <li>Kullanıcı davranış analizi</li>
                    <li>Performans ölçümleri</li>
                  </ul>
                </div>

                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h3 className="text-lg font-semibold text-purple-400 mb-2">Pazarlama Çerezleri</h3>
                  <p className="text-slate-300 text-sm mb-2">
                    İlgi alanlarınıza uygun içerik ve reklamlar göstermek için kullanılır.
                  </p>
                  <ul className="list-disc list-inside text-slate-400 text-sm space-y-1">
                    <li>Reklam hedefleme</li>
                    <li>Sosyal medya entegrasyonları</li>
                    <li>Dönüşüm takibi</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">3. Kullandığımız Çerezlerin Listesi</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-white">Çerez Adı</th>
                      <th className="text-left py-3 px-4 text-white">Tür</th>
                      <th className="text-left py-3 px-4 text-white">Süre</th>
                      <th className="text-left py-3 px-4 text-white">Amaç</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4">session_token</td>
                      <td className="py-3 px-4">Zorunlu</td>
                      <td className="py-3 px-4">Oturum</td>
                      <td className="py-3 px-4">Kullanıcı oturumu yönetimi</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4">auth_token</td>
                      <td className="py-3 px-4">Zorunlu</td>
                      <td className="py-3 px-4">7 gün</td>
                      <td className="py-3 px-4">Kimlik doğrulama</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4">theme_pref</td>
                      <td className="py-3 px-4">İşlevsel</td>
                      <td className="py-3 px-4">1 yıl</td>
                      <td className="py-3 px-4">Tema tercihi</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4">_ga</td>
                      <td className="py-3 px-4">Analitik</td>
                      <td className="py-3 px-4">2 yıl</td>
                      <td className="py-3 px-4">Google Analytics</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-3 px-4">cookie_consent</td>
                      <td className="py-3 px-4">Zorunlu</td>
                      <td className="py-3 px-4">1 yıl</td>
                      <td className="py-3 px-4">Çerez tercihleriniz</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">4. Çerezleri Yönetme</h2>
              <p className="text-slate-300 mb-4">
                Çerez tercihlerinizi aşağıdaki yöntemlerle yönetebilirsiniz:
              </p>
              
              <h3 className="text-xl font-semibold text-white mb-3">4.1 Tarayıcı Ayarları</h3>
              <p className="text-slate-300 mb-4">
                Çoğu tarayıcı, çerezleri kabul etme veya reddetme seçeneği sunar. Tarayıcınızın 
                ayarlar menüsünden çerez tercihlerinizi değiştirebilirsiniz.
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 mb-4">
                <li><strong className="text-white">Chrome:</strong> Ayarlar → Gizlilik ve güvenlik → Çerezler</li>
                <li><strong className="text-white">Firefox:</strong> Seçenekler → Gizlilik ve Güvenlik</li>
                <li><strong className="text-white">Safari:</strong> Tercihler → Gizlilik</li>
                <li><strong className="text-white">Edge:</strong> Ayarlar → Çerezler ve site izinleri</li>
              </ul>

              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <p className="text-slate-300 text-sm">
                  <strong className="text-yellow-200">Not:</strong> Zorunlu çerezleri devre dışı bırakmak, 
                  web sitesinin düzgün çalışmamasına neden olabilir.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">5. Üçüncü Taraf Çerezleri</h2>
              <p className="text-slate-300 mb-4">
                Sitemizde aşağıdaki üçüncü taraf hizmetlerinin çerezleri bulunabilir:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li><strong className="text-white">Google Analytics:</strong> Site trafiği analizi</li>
                <li><strong className="text-white">Stripe:</strong> Ödeme işlemleri</li>
              </ul>
              <p className="text-slate-300 mt-4">
                Bu hizmetlerin kendi gizlilik politikaları bulunmaktadır ve çerez kullanımları 
                kendi politikalarına tabidir.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">6. Değişiklikler</h2>
              <p className="text-slate-300">
                Bu çerez politikası zaman zaman güncellenebilir. Önemli değişiklikler yapıldığında 
                sizi bilgilendireceğiz. Güncel politikayı düzenli olarak kontrol etmenizi öneririz.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. İletişim</h2>
              <p className="text-slate-300 mb-4">
                Çerez politikamız hakkında sorularınız için bizimle iletişime geçebilirsiniz:
              </p>
              <div className="p-4 rounded-lg bg-slate-800/50">
                <p className="text-slate-300 mb-2">
                  <strong className="text-white">Şirket:</strong> Datex Bilişim AŞ
                </p>
                <p className="text-slate-300 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <strong className="text-white">E-posta:</strong> info@borsamex.com
                </p>
              </div>
            </section>

          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
          <Link to="/legal/privacy" className="text-slate-400 hover:text-primary transition-colors">
            Gizlilik Politikası
          </Link>
          <span className="text-slate-600">•</span>
          <Link to="/legal/terms" className="text-slate-400 hover:text-primary transition-colors">
            Kullanım Koşulları
          </Link>
          <span className="text-slate-600">•</span>
          <Link to="/legal/risk" className="text-slate-400 hover:text-primary transition-colors">
            Risk Uyarısı
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
